import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Typography, Stack } from "@mui/material";
import { useLanguage } from "../context/LanguageContext";
import AuthShell from "../components/AuthShell";
import { alpha } from "@mui/material/styles";
import { ALLOW_GUEST_BROWSING } from "../config";
import logo from "url:../../assets/logo.png";

const NAVY    = "#0F2156";
const CRIMSON = "#B91C2C";
const GOLD    = "#E8A020";


export default function Landing() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const headerRight = (
    <Button
      variant="outlined"
      size="small"
      onClick={() => navigate("/login")}
      sx={{
        color: "white",
        borderColor: alpha("#fff", 0.4),
        fontWeight: 600,
        textTransform: "none",
        fontSize: "0.82rem",
        "&:hover": { borderColor: "white", bgcolor: alpha("#fff", 0.08) },
      }}
    >
      {t("landing.login")}
    </Button>
  );

  return (
    <AuthShell rightContent={headerRight}>

      {/* ── Hero ── */}
      <Box
        sx={{
          position: "relative",
          background: `linear-gradient(145deg, ${NAVY} 0%, #1a3a7a 45%, ${alpha(CRIMSON, 0.9)} 100%)`,
          color: "white",
          py: { xs: 8, md: 12 },
          px: 2,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          "&::before": {
            content: '""',
            position: "absolute",
            top: -80, right: -80,
            width: 320, height: 320,
            borderRadius: "50%",
            background: alpha(GOLD, 0.12),
            filter: "blur(60px)",
            pointerEvents: "none",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: -60, left: -60,
            width: 240, height: 240,
            borderRadius: "50%",
            background: alpha(CRIMSON, 0.15),
            filter: "blur(50px)",
            pointerEvents: "none",
          },
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="Sajha Social"
          sx={{
            height: { xs: 180, md: 240 },
            width: "auto",
            display: "block",
            mixBlendMode: "screen",
            position: "relative",
            zIndex: 1,
            mb: 3,
          }}
        />

        {/* Badge */}
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            px: 2, py: 0.6,
            borderRadius: 20,
            bgcolor: alpha(GOLD, 0.18),
            border: `1px solid ${alpha(GOLD, 0.35)}`,
            mb: 2.5,
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: GOLD }} />
          <Typography
            variant="caption"
            sx={{ color: GOLD, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", fontSize: "0.7rem" }}
          >
            {t("landing.communityPlatform")}
          </Typography>
        </Box>

        <Typography
          variant="h6"
          sx={{ opacity: 0.9, mb: 1.5, fontWeight: 500, position: "relative", zIndex: 1, textAlign: "center" }}
        >
          {t("landing.tagline")}
        </Typography>

        <Typography
          variant="body1"
          sx={{ opacity: 0.8, maxWidth: 520, mb: 5, lineHeight: 1.75, position: "relative", zIndex: 1, textAlign: "center" }}
        >
          {t("landing.whatIsDescription")}
        </Typography>

        <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ position: "relative", zIndex: 1 }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/signup")}
            sx={{
              background: `linear-gradient(135deg, #DC2626 0%, ${CRIMSON} 100%)`,
              color: "white",
              fontWeight: 800,
              px: 3.5,
              borderRadius: 12,
              fontSize: "0.95rem",
              boxShadow: `0 4px 20px ${alpha(CRIMSON, 0.5)}`,
              "&:hover": {
                background: `linear-gradient(135deg, ${CRIMSON} 0%, #8B0F1B 100%)`,
                boxShadow: `0 6px 28px ${alpha(CRIMSON, 0.6)}`,
              },
            }}
          >
            {t("landing.getStarted")}
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate("/login")}
            sx={{
              borderColor: alpha("#fff", 0.5),
              color: "white",
              px: 3.5,
              borderRadius: 12,
              fontSize: "0.95rem",
              backdropFilter: "blur(4px)",
              "&:hover": { borderColor: "white", bgcolor: alpha("#fff", 0.12) },
            }}
          >
            {t("landing.login")}
          </Button>
        </Stack>

        {ALLOW_GUEST_BROWSING && (
          <Button
            variant="text"
            onClick={() => navigate("/app/feed")}
            sx={{
              mt: 2,
              color: alpha("#fff", 0.85),
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.9rem",
              position: "relative",
              zIndex: 1,
              "&:hover": { color: "white", bgcolor: alpha("#fff", 0.08) },
            }}
          >
            {t("landing.browseAsGuest")} →
          </Button>
        )}
      </Box>

      {/* ── Stats strip ── */}
      <Box
        sx={{
          background: `linear-gradient(90deg, ${NAVY} 0%, #1e3f8a 100%)`,
          py: { xs: 4, md: 5 },
          px: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 3, sm: 0 }}
          justifyContent="space-evenly"
          alignItems="center"
          sx={{ maxWidth: 700, mx: "auto" }}
        >
          {[
            { value: "🌍", label: language === "ne" ? "विश्वभर उपलब्ध" : "Available worldwide" },
            { value: "🇳🇵", label: language === "ne" ? "नेपालीहरूको लागि" : "Built for Nepalis" },
          ].map(({ value, label }) => (
            <Box key={label} textAlign="center">
              <Typography fontSize="1.8rem" mb={0.3}>{value}</Typography>
              <Typography variant="caption" sx={{ color: alpha("#fff", 0.8), fontWeight: 600, letterSpacing: "0.04em" }}>
                {label}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* ── CTA banner ── */}
      <Box
        sx={{
          py: { xs: 7, md: 9 },
          px: 2,
          textAlign: "center",
          bgcolor: "white",
        }}
      >
        <Typography variant="h5" fontWeight={800} color={NAVY} mb={1.5}>
          {t("landing.ctaTitle")}
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={4} sx={{ maxWidth: 460, mx: "auto", lineHeight: 1.7 }}>
          {t("landing.ctaSubtitle")}
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate("/signup")}
          sx={{
            background: `linear-gradient(135deg, #DC2626 0%, ${CRIMSON} 100%)`,
            color: "white",
            fontWeight: 800,
            px: 4,
            py: 1.4,
            borderRadius: 12,
            fontSize: "0.95rem",
            boxShadow: `0 4px 20px ${alpha(CRIMSON, 0.4)}`,
            "&:hover": {
              background: `linear-gradient(135deg, ${CRIMSON} 0%, #8B0F1B 100%)`,
              boxShadow: `0 6px 28px ${alpha(CRIMSON, 0.55)}`,
            },
          }}
        >
          {t("landing.ctaButton")}
        </Button>
      </Box>

      {/* ── Footer ── */}
      <Box
        component="footer"
        sx={{
          bgcolor: NAVY,
          color: alpha("#fff", 0.65),
          py: 4,
          px: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "center", sm: "flex-start" }}
          spacing={3}
          sx={{ maxWidth: 800, mx: "auto", mb: 3 }}
        >
          {/* Brand blurb */}
          <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
            <Typography variant="subtitle2" fontWeight={700} color="white" mb={0.5}>
              {t("common.appName")}
            </Typography>
            <Typography variant="caption" sx={{ maxWidth: 220, display: "block", lineHeight: 1.6 }}>
              {t("landing.tagline")}
            </Typography>
          </Box>

          {/* Links */}
          <Typography
            variant="caption"
            component="a"
            onClick={() => navigate("/terms")}
            sx={{
              color: alpha("#fff", 0.6),
              cursor: "pointer",
              textDecoration: "none",
              fontWeight: 500,
              "&:hover": { color: "white" },
              transition: "color 0.15s",
            }}
          >
            {t("landing.footer.terms")}
          </Typography>
        </Stack>

      </Box>

    </AuthShell>
  );
}
